import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageFlag } from "@/components/ui/language-flag";
import type { Language, Translations } from "@/lib/i18n/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

function isLanguage(value: string): value is Language {
  return value === "et" || value === "en";
}

/**
 * The language the protocol itself is printed in, chosen here and independent
 * of the interface — a protocol is filed and circulated, so its language is a
 * property of the document rather than of whoever generated it. Same reasoning
 * as the template forms' own PDF-language picker.
 *
 * One flag, the chosen language's, opening a menu of the others. The language
 * names are the trigger's accessible name and tooltip and the menu's labels,
 * for anyone who cannot see or does not recognise a flag.
 */
export function ProtocolLanguagePicker({
  value,
  copy,
  onChange,
}: {
  value: Language;
  copy: ProtocolCopy;
  onChange: (language: Language) => void;
}) {
  const options: Array<{ language: Language; name: string }> = [
    { language: "et", name: copy.pdfLanguageEt },
    { language: "en", name: copy.pdfLanguageEn },
  ];
  const current = options.find((option) => option.language === value) ?? options[0];
  const label = `${copy.pdfLanguage}: ${current.name}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={label} title={label}>
          <LanguageFlag language={value} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {copy.pdfLanguage}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (isLanguage(next)) onChange(next);
          }}
        >
          {options.map(({ language, name }) => (
            <DropdownMenuRadioItem key={language} value={language} className="gap-2">
              <LanguageFlag language={language} />
              {name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
